import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule, MatSelectTrigger } from '@angular/material/select';
import { CarAvailability } from '../../../core/models/car.model';
import { StatusLabelPipe } from '../../pipes/status-label.pipe';

/**
 * CVA-обёртка над `<mat-select>` для выбора автомобиля из списка доступных.
 *
 * Value: `number | null` (car_id).
 *
 * Наследует валидацию с родительского `FormControl` (через `Validators.required`),
 * а также показывает server-ошибку, которую бэкенд прислал на `car` (через
 * `parentErrors?.['serverError']` — аналогично `datetime-input`).
 *
 * Внутри `<mat-option>` статус показывается стилизованным `<span>` (а не
 * `<mat-chip>`), потому что `mat-chip` интерактивен (обрабатывает клики/фокус/риппл)
 * и перехватывает событие выбора опции — это известное ограничение Material.
 */
@Component({
  selector: 'app-car-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatSelectTrigger,
    StatusLabelPipe,
  ],
  templateUrl: './car-select.component.html',
  styleUrl: './car-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CarSelectComponent),
    },
  ],
})
export class CarSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() cars: CarAvailability[] = [];
  @Input() label = 'Автомобиль';
  @Input() required = false;

  readonly control = new FormControl<number | null>(null);

  onChange: (v: number | null) => void = () => {};
  onTouched: () => void = () => {};

  private readonly injector = inject(Injector);
  private ngControl: NgControl | null = null;
  private subscription = this.control.valueChanges.subscribe((v) => {
    this.onChange(v);
  });

  private get parentErrors(): Record<string, any> | null {
    const control = this.ngControl?.control as FormControl | null;
    return control?.errors ?? null;
  }

  ngOnInit(): void {
    // Отложенное получение NgControl — разрывает цикл DI (см. datetime-input).
    try {
      this.ngControl = this.injector.get(NgControl, null, { self: true });
    } catch {
      this.ngControl = null;
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // ── CVA-методы ────────────────────────────────────────────────────
  writeValue(value: number | null): void {
    this.control.patchValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (v: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  // ── View helpers (для шаблона) ────────────────────────────────────
  get selectedCar(): CarAvailability | null {
    const v = this.control.value;
    if (v == null) return null;
    return this.cars.find((c) => c.car_id === v) ?? null;
  }

  showServerError(): boolean {
    const errors = this.parentErrors;
    if (!errors) return false;
    return !!errors['serverError'];
  }

  serverError(): string {
    const errors = this.parentErrors;
    if (!errors) return '';
    return errors['serverError'] ? String(errors['serverError']) : '';
  }
}
